import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useParams } from "react-router-dom";
import { lazy, Suspense } from "react";
import { AuthProvider } from "@/contexts/AuthContext";
import { MerchantProvider } from "@/contexts/MerchantContext";
import { BrandingProvider } from "@/contexts/BrandingContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import DashboardLayout from "@/components/DashboardLayout";
import { Loader2 } from "lucide-react";

// Lazy load route components
const Auth = lazy(() => import("./pages/Auth"));
const ForgotPassword = lazy(() => import("./pages/ForgotPassword"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Dashboard = lazy(() => import("./pages/Dashboard"));
const Merchants = lazy(() => import("./pages/Merchants"));
const MerchantDetail = lazy(() => import("./pages/MerchantDetail"));
const Payments = lazy(() => import("./pages/Payments"));
const Subscriptions = lazy(() => import("./pages/Subscriptions"));
const InternalSubscriptions = lazy(() => import("./pages/InternalSubscriptions"));
const Transactions = lazy(() => import("./pages/Transactions"));
const Analytics = lazy(() => import("./pages/Analytics"));
const Settings = lazy(() => import("./pages/Settings"));
const AuditLogs = lazy(() => import("./pages/AuditLogs"));
const PublicPayment = lazy(() => import("./pages/PublicPayment"));
const PublicCampaign = lazy(() => import("./pages/PublicCampaign"));
const AdminUsers = lazy(() => import("./pages/AdminUsers"));
const SuperAdminUsers = lazy(() => import("./pages/SuperAdminUsers"));
const Customers = lazy(() => import("./pages/Customers"));
const CustomerDetail = lazy(() => import("./pages/CustomerDetail"));
const Templates = lazy(() => import("./pages/Templates"));
const Tenants = lazy(() => import("./pages/Tenants"));
const Pricing = lazy(() => import("./pages/Pricing"));
const SubscriptionManagement = lazy(() => import("./pages/SubscriptionManagement"));
const Invoices = lazy(() => import("./pages/Invoices"));
const Notifications = lazy(() => import("./pages/Notifications"));
const Debts = lazy(() => import("./pages/Debts"));
const Reconciliation = lazy(() => import("./pages/Reconciliation"));
const NotFound = lazy(() => import("./pages/NotFound"));

// Component to redirect /:tenantSlug to /:tenantSlug/auth
const TenantRedirect = () => {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  return <Navigate to={`/${tenantSlug}/auth`} replace />;
};
const queryClient = new QueryClient();

// Loading fallback component
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <Loader2 className="w-8 h-8 animate-spin text-primary" />
  </div>
);

// Define protected route configurations
const protectedRoutes = [
  { path: "dashboard", element: <Dashboard />, roles: undefined },
  { path: "merchants", element: <Merchants />, roles: ['super_user', 'admin'] },
  { path: "merchants/:merchantId", element: <MerchantDetail />, roles: ['super_user', 'admin'] },
  { path: "customers", element: <Customers />, roles: undefined },
  { path: "customers/:customerId", element: <CustomerDetail />, roles: undefined },
  { path: "payments", element: <Payments />, roles: ['super_user', 'admin', 'full_user'] },
  { path: "invoices", element: <Invoices />, roles: ['super_user', 'admin', 'full_user'] },
  { path: "internal-subscriptions", element: <InternalSubscriptions />, roles: undefined },
  { path: "transactions", element: <Transactions />, roles: undefined },
  { path: "analytics", element: <Analytics />, roles: undefined },
  { path: "notifications", element: <Notifications />, roles: undefined },
  { path: "debts", element: <Debts />, roles: undefined },
  { path: "reconciliation", element: <Reconciliation />, roles: ['super_user', 'admin', 'full_user'] },
  { path: "subscriptions", element: <Subscriptions />, roles: undefined },
  { path: "settings", element: <Settings />, roles: undefined },
  { path: "templates", element: <Templates />, roles: undefined },
  { path: "audit-logs", element: <AuditLogs />, roles: ['super_user', 'admin'] },
  { path: "admin/users", element: <AdminUsers />, roles: ['super_user', 'admin'] },
  { path: "tenants", element: <Tenants />, roles: ['super_user'] },
  { path: "super-admin/users", element: <SuperAdminUsers />, roles: ['super_user'] },
  { path: "subscription-management", element: <SubscriptionManagement />, roles: ['super_user'] },
];

// Helper to render protected routes
const renderProtectedRoute = (route: typeof protectedRoutes[0], withTenantPrefix: boolean = false) => {
  const path = withTenantPrefix ? `:tenantSlug/${route.path}` : route.path;
  return (
    <Route
      key={path}
      path={path}
      element={
        <ProtectedRoute allowedRoles={route.roles as any}>
          <DashboardLayout>{route.element}</DashboardLayout>
        </ProtectedRoute>
      }
    />
  );
};

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <BrandingProvider>
            <MerchantProvider>
              <Suspense fallback={<PageLoader />}>
                <Routes>
                  <Route path="/" element={<Navigate to="/dashboard" replace />} />
                  
                  {/* Auth routes - support both generic and tenant-specific URLs */}
                  <Route path="/auth" element={<Auth />} />
                  <Route path="/:tenantSlug/auth" element={<Auth />} />
                  
                  {/* Redirect bare tenant slug to auth page */}
                  <Route path="/:tenantSlug" element={<TenantRedirect />} />
                  
                  {/* Password recovery routes - support both generic and tenant-specific URLs */}
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/:tenantSlug/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/:tenantSlug/reset-password" element={<ResetPassword />} />
                  
                  <Route path="/pricing" element={<Pricing />} />
                  
                  {/* Public payment link entrypoints */}
                  <Route path="/pay/*" element={<PublicPayment />} />
                  <Route path="/public-payment/*" element={<PublicPayment />} />
                  
                  {/* Public campaign page */}
                  <Route path="/campaign" element={<PublicCampaign />} />
                  
                  {/* Protected routes WITHOUT tenant prefix (for backwards compatibility) */}
                  {protectedRoutes.map(route => renderProtectedRoute(route, false))}
                  
                  {/* Protected routes WITH tenant prefix (new tenant-scoped URLs) */}
                  {protectedRoutes.map(route => renderProtectedRoute(route, true))}
                  
                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </MerchantProvider>
          </BrandingProvider>
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
